// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import {VNode} from 'snabbdom';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {formatPhoneNumber, parsePhoneNumber} from 'common/util/string';
import {ENTER_KEY_CODE, ESCAPE_KEY_CODE} from 'common/util/dom';
import {TagSize} from 'common/components/tag/types';
import {IronContextRegistry} from 'common/report/context-registry';

import {InputType, SettingsLiAttrs, SettingsLiParams, SettingsLiState} from './types';
import {template} from './template';
import css from './index.inline.styl';

export type SettingsLiJsxProps = {
  attrs?: SettingsLiAttrs;
  params?: SettingsLiParams;
  props?: {
    updateFunc?: (value) => Promise<boolean>;
    sanitizeLabels?: (value: string[]) => string[];
    selectItems?;
    infoRenderer?: () => VNode | null;
  };
  on?: {
    'button-click'?: (ev) => void;
    'switch-change'?: (ev) => void;
    cancelClick?: (ev) => void;
    editClick?: (ev) => void;
    saveClick?: (ev) => void;
  };
};

export class SettingsLi extends Component<
  SettingsLiState,
  SettingsLiAttrs,
  unknown,
  unknown,
  IronContextRegistry,
  SettingsLiParams
> {
  private declare _infoRenderer;
  static get attrsSchema(): AttrsSchema<SettingsLiAttrs> {
    return {
      ...super.attrsSchema,
      // This component has way too many attributes, some of which are conflicting and is need of a refactor
      'all-tags': `json`,
      button: `string`,
      'button-icon': `string`,
      buttontext: `string`,
      'button-theme': `string`,
      'custom-handling': `boolean`,
      'delete-button': `boolean`,
      disabled: `boolean`,
      'disabled-message': `string`,
      editable: `boolean`,
      editing: `boolean`,
      emptiable: `boolean`,
      fileaccept: {type: `string`, default: `*`},
      'hide-buttons': `boolean`,
      isfile: {type: `boolean`, description: `DEPRECATED: use 'type'`},
      label: `string`,
      maxlength: {type: `number`, default: `null`},
      'multiline-input': `boolean`,
      placeholder: `string`,
      'read-only-tags': `json`,
      removable: `boolean`,
      'selected-tags': `json`,
      'tag-size': {type: `string`, default: TagSize.Small, enum: Object.values(TagSize)},
      'static-tags': `boolean`,
      tooltip: `string`,
      info: `string`,
      switchon: `boolean`,
      type: {
        type: `string`,
        default: ``,
        enum: Object.values(InputType),
      },
      value: `string`,
      'fire-immediate-action': {
        type: `boolean`,
        description: `Set to true if you only want to handle a custom event when the edit button is clicked`,
      },
      'format-text': `boolean`,
      'text-area-rows': `number`,
      'text-area-resize': `string`,
    };
  }

  public set infoRenderer(value) {
    this._infoRenderer = value;
    this.update();
  }

  public get infoRenderer() {
    return this._infoRenderer;
  }

  get config() {
    return {
      css,
      template,
      contexts: [`i18n` as const],
      useShadowDom: true,
      params: {
        decimalPrecisionValue: Number,
        decimalPrecisionUpdateFunc: Function,
      },
      defaultParams: {
        decimalPrecisionValue: null,
      },
      defaultState: {
        editing: false,
        password1: ``,
        password2: ``,
        countryCode: ``,
        localPhoneNumber: ``,
        type: InputType.Text,
        isFile: false,
        allTags: new Set<string>(),
        readonlyTags: new Set<string>(),
        selectedTags: new Set<string>(),
        originalValue: null,
        originalSelectedTags: new Set<string>(),
        inputValue: ``,
        saving: false,
        selectItems: [],
        selectMenuOpen: false,
        selectTagOpen: false,
        selectedValue: {},
        uploading: false,
        saveDisabled: false,
        removable: false,
        validationError: null,
        stagedDecimalPrecision: undefined,
        stagedAbbrev: null,
      },

      helpers: {
        tagSelectorChanged: (ev) => {
          const tagName = ev.detail.tagName;
          const selectedTags = this.state.selectedTags;
          const allTags = this.state.allTags;
          if (ev.detail.action === `addTag`) {
            this.update({
              selectedTags: (selectedTags.add(tagName), selectedTags),
              allTags: (allTags.add(tagName), allTags),
            });
          } else if (ev.detail.action === `removeTag`) {
            this.update({selectedTags: (selectedTags.delete(tagName), selectedTags)});
          }
        },
        switchChanged: (ev) => {
          this.dispatchEvent(new CustomEvent(`switchChange`, {detail: ev.detail}));
        },
        onAbbrevChange: (ev) => {
          this.update({stagedAbbrev: ev.detail.on});
        },
        onAbbrevCancel: () => {
          this.update({stagedAbbrev: this.attr(`switchon`), editing: false});
        },
        onAbbrevSave: () => {
          this.update({saving: true});
          this.dispatchEvent(new CustomEvent(`switchChange`, {detail: {on: this.state.stagedAbbrev}}));
          this.update({stagedAbbrev: null, saving: false, editing: false});
        },
        onDecimalPrecisionChange: (val) => {
          this.update({stagedDecimalPrecision: val});
        },
        onDecimalPrecisionCancel: () => {
          this.update({stagedDecimalPrecision: this.params.decimalPrecisionValue, editing: false});
        },
        onDecimalPrecisionSave: () => {
          this.update({saving: true});
          this.params.decimalPrecisionUpdateFunc(this.state.stagedDecimalPrecision);
          this.update({stagedDecimalPrecision: undefined, saving: false, editing: false});
        },
        handleKeydown: (ev) => {
          const saveHandler = this.attr(`custom-handling`) ? this.helpers.customSave : this.helpers.clickedSave;
          const cancelHandler = this.attr(`custom-handling`) ? this.helpers.customCancel : this.helpers.stopEditing;
          if (ev.keyCode === ENTER_KEY_CODE) {
            saveHandler(ev);
            ev.preventDefault();
          } else if (ev.keyCode === ESCAPE_KEY_CODE) {
            cancelHandler(ev);
          }
        },
        clickedButton: () => this.dispatchEvent(new CustomEvent(`button-click`)),
        clickedSave: async () => {
          let value: any = this.state.inputValue;
          if (this.isSelect) {
            value =
              this.attr(`type`) === InputType.MultiSelect
                ? {statusItems: this.state.selectItems, statusValue: this.state.inputValue}
                : this.state.selectedValue;
          } else if (this.attr(`type`) === InputType.Password) {
            value = this.state.password1;
          } else if (this.attr(`type`) === `tag`) {
            value = this.state.selectedTags;
          } else if (this.state.type === InputType.PhoneNumber) {
            value = {countryCode: this.state.countryCode, localPhoneNumber: this.state.localPhoneNumber};
          }

          if (this.validator) {
            try {
              const res = this.validator(value);
              if (res.error) {
                this.update({validationError: res.error});
                return;
              }
              this.update({validationError: ``});
            } catch (err) {
              this.update({validationError: err.message});
              return;
            }
          }

          this.update({saving: true});
          const succeeded = await this.updateFunc(value);
          if (succeeded) {
            this.update({editing: false});
          }
          this.updateInput();
          this.update({saving: false});
        },
        clickedUpload: () => {
          this.fileEl.click();
        },
        clickedRemove: (ev) => {
          this.dispatchEvent(new CustomEvent(`removeClick`, {detail: ev.detail}));
        },
        inputType: () =>
          this.attr(`type`) || (this.state.isFile ? InputType.File : this.isSelect ? InputType.Select : InputType.Text),
        isSaveableInput: () => {
          switch (this.helpers.inputType()) {
            case InputType.Text: {
              return !!this.state.inputValue || this.attr(`emptiable`);
            }
            case InputType.Password: {
              return !!this.state.password1 && this.state.password1 === this.state.password2;
            }
            case InputType.PhoneNumber: {
              return !!this.state.localPhoneNumber && !!this.state.countryCode;
            }
            default: {
              return true;
            }
          }
        },
        label: () => this.attr(`label`),
        startEditing: async () => {
          if (this.preEdit && !(await this.preEdit())) {
            return false;
          }

          if (this.attr(`fire-immediate-action`)) {
            return this.dispatchEvent(new CustomEvent(`immediateAction`));
          }

          let updateProps: Partial<SettingsLiState> = {
            editing: true,
            selectMenuOpen: true,
            selectTagOpen: true,
            validationError: null,
            password1: ``,
            password2: ``,
          };
          if (this.state.type === InputType.PhoneNumber) {
            try {
              const {countryCode, localPhoneNumber} = parsePhoneNumber(this.state.inputValue);
              updateProps = {...updateProps, countryCode, localPhoneNumber, originalValue: this.state.inputValue};
            } catch (err) {
              this.update({...updateProps, validationError: err.message});
            }
          } else if (this.state.type === InputType.MultiSelect) {
            updateProps = {...updateProps, originalValue: this.state.inputValue};
          }

          this.update(updateProps);

          requestAnimationFrame(() => this.inputEl?.focus());
          return this.dispatchEvent(new CustomEvent(`editClicked`));
        },
        stopEditing: () => {
          this.updateInput();

          const updateProps: Partial<SettingsLiState> = {
            editing: false,
            selectMenuOpen: false,
            selectTagOpen: false,
            validationError: null,
            password1: ``,
            password2: ``,
            countryCode: ``,
            localPhoneNumber: ``,
          };
          if (this.state.type === InputType.PhoneNumber) {
            updateProps.inputValue = this.state.originalValue;
            updateProps.originalValue = ``;
          } else if (this.state.type === InputType.MultiSelect) {
            updateProps.inputValue = this.state.originalValue;
          }

          this.update(updateProps);
        },
        stopUploading: () => {
          this.update({uploading: false});
        },
        handleTagMenuChange: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({
              selectTagOpen: false,
              selectedTags: this.state.originalSelectedTags,
              originalSelectedTags: new Set(),
            });
            this.helpers.stopEditing();
          } else if (ev.detail.state === `open`) {
            this.state.originalSelectedTags = new Set(this.state.selectedTags);
          }
        },
        handleSelectMenuChange: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({selectMenuOpen: false});
          }
        },
        selectItem: (ev) => this.update({selectedValue: ev.detail.item, selectMenuOpen: false}),
        multiSelectItem: (ev) => {
          const selectItems = [...this.state.selectItems];
          const item = selectItems.findIndex((selectItem) => ev.detail.item.label === selectItem.label);
          selectItems[item].isSelected = !selectItems[item].isSelected;
          let labels = selectItems
            .filter((val) => {
              return val.isSelected;
            })
            .map((val) => {
              return val.label;
            });
          if (this.sanitizeLabels) {
            labels = this.sanitizeLabels(labels);
          }
          const inputValue = labels.join(`, `);
          this.update({selectItems, inputValue});
        },
        toggleSelectMenu: () => this.update({selectMenuOpen: !this.state.selectMenuOpen}),

        getTagOptions: () =>
          Array.from(
            new Set([
              ...Array.from(this.state.allTags),
              ...Array.from(this.state.readonlyTags),
              ...Array.from(this.state.selectedTags),
            ]),
          ).map((label) => ({
            label,
            value: label,
            isSelected: this.state.selectedTags.has(label),
            isDisabled: this.state.readonlyTags.has(label),
          })),

        fileInserted: (vnode) => {
          this.fileEl = vnode.elm;
        },
        fileDialogEnded: (ev) => {
          this.update({saving: true});
          this.dispatchEvent(new CustomEvent(`fileAdded`, {detail: ev.target}));
        },
        inputInserted: (vnode) => (this.inputEl = vnode.elm),
        inputUpdated: (ev) => {
          this.update({inputValue: ev.target.value, validationError: null});
          this.dispatchEvent(new CustomEvent(`inputUpdate`, {detail: ev.detail}));
        },
        passwordUpdated: (num, ev) => this.update({[`password${num}`]: ev.target.value}),
        phoneNumberUpdated: (ev) => this.phoneNumberUpdated(ev),
        uploadFile: () => {
          this.update({uploading: true});
        },
        customSave: (ev) => {
          switch (this.helpers.inputType()) {
            case InputType.AbbrevToggle: {
              this.helpers.onAbbrevSave();
              break;
            }
            case InputType.DecimalPrecision: {
              this.helpers.onDecimalPrecisionSave();
              break;
            }
          }
          this.dispatchEvent(new CustomEvent(`saveClick`, {detail: ev.detail}));
        },
        customCancel: (ev) => {
          switch (this.helpers.inputType()) {
            case InputType.AbbrevToggle: {
              this.helpers.onAbbrevCancel();
              break;
            }
            case InputType.DecimalPrecision: {
              this.helpers.onDecimalPrecisionCancel();
              break;
            }
          }
          this.dispatchEvent(new CustomEvent(`cancelClick`, {detail: ev.detail}));
        },
        parsePhoneNumber: (phoneNumber) => parsePhoneNumber(phoneNumber),
      },
    };
  }

  declare fileEl: HTMLInputElement;
  declare inputEl: HTMLInputElement;

  declare isSelect: boolean;
  declare preEdit: () => Promise<boolean>;
  declare validator: (value: any) => any;
  declare updateFunc: (value: any) => Promise<boolean>;
  declare sanitizeLabels: (value: string[]) => string[];

  private declare _rawSelectItems: any[];

  connectedCallback() {
    super.connectedCallback();
    this.updateInput();
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `value`) {
      this.updateInput();
    } else if (attr === `isfile`) {
      this.update({isFile: this.attr(`isfile`)});
    } else if (attr === `editing`) {
      this.update({editing: this.attr(`editing`)});
    } else if (attr === `removable`) {
      this.update({removable: this.attr(`removable`)});
    } else if (attr === `all-tags`) {
      this.update({allTags: new Set(this.attr(`all-tags`))});
    } else if (attr === `selected-tags`) {
      this.update({selectedTags: new Set(this.attr(`selected-tags`))});
    } else if (attr === `read-only-tags`) {
      this.update({readonlyTags: new Set(this.attr(`read-only-tags`))});
    } else if (attr === `type`) {
      this.update({type: this.attr(`type`)});
    }
  }

  updateInput() {
    const value = this.attr(`value`);
    if (this.isSelect) {
      const updateProps = {
        selectedValue: this._rawSelectItems.find((item) => item.id?.toString() === value),
        selectItems: this._rawSelectItems.map((item) =>
          Object.assign({isSelected: item.id?.toString() === value}, item),
        ),
      };
      if (this.attr(`type`) === InputType.MultiSelect) {
        updateProps[`inputValue`] = this.attr(`value`);
      }
      this.update(updateProps);
    } else if (this.state.isFile) {
      this.update({inputValue: value, removable: !!value});
    } else if (this.state.type === InputType.PhoneNumber && this.state.editing) {
      const phoneNumber = formatPhoneNumber(this.state.countryCode, this.state.localPhoneNumber, false);
      if (phoneNumber) {
        this.update({inputValue: phoneNumber});
      }
    } else {
      this.update({inputValue: value});
    }
  }

  phoneNumberUpdated(ev) {
    if (ev.target.name === `country-code`) {
      this.update({countryCode: ev.target.value});
    } else if (ev.target.name === `local-phone-number`) {
      this.update({localPhoneNumber: ev.target.value});
    }
  }

  set selectItems(val) {
    this.isSelect = true;
    this._rawSelectItems = val;
    this.updateInput();
  }
}

export const SettingsLiEl = defineAndMakeJsxFactory<SettingsLiJsxProps>(`mp-settings-li`, SettingsLi);
export const SettingsLiReact = panel2react<SettingsLiJsxProps>(`mp-settings-li`, SettingsLi);
